const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID;

// Canonical origin used for both rel=canonical and the synthesized
// page_location sent to GA4 / Google Ads. Hardcoded because the app is only
// ever served from this hostname in production; dev/local builds still see a
// canonical pointing at production, which is correct — crawlers should never
// index dev.plum-app.com or localhost.
const CANONICAL_ORIGIN = 'https://www.plum-app.com';

// Conversion labels supplied by Google Ads. The full `send_to` value is
// `${GOOGLE_ADS_ID}/${label}`.
export const GOOGLE_ADS_CONVERSIONS = {
  sign_up: 'kuIHCMCRsKUcEKWrv8VD',
  purchase: 'yHqjCLqRsKUcEKWrv8VD',
  subscribe: '4BNcCL2RsKUcEKWrv8VD',
};

// Collapse per-session and per-owner URL segments into route templates so the
// GA4 Pages and Screens report groups by route rather than by individual
// session id. Without this, every session creates its own row in Pages
// reports — useless for understanding which tabs/screens users visit.
//   /session/<id>            -> /session/:id
//   /session/<id>/<tab>      -> /session/:id/<tab>
//   /session/new/...         -> left alone (literal route, not an id)
//   /shared/<owner>/<id>/... -> /shared/:owner/:id/...
// Query strings and fragments are preserved verbatim — GA4 needs gclid/utm_*
// in page_location to attribute the session source on first hit.
export function normalizePath(rawPath) {
  if (!rawPath || typeof rawPath !== 'string') return rawPath;
  const queryIdx = rawPath.search(/[?#]/);
  const pathOnly = queryIdx === -1 ? rawPath : rawPath.slice(0, queryIdx);
  const tail = queryIdx === -1 ? '' : rawPath.slice(queryIdx);
  let p = pathOnly.replace(/\/+$/, '') || '/';
  p = p.replace(/^\/session\/(?!new(?:\/|$))[^/]+/, '/session/:id');
  p = p.replace(/^\/shared\/[^/]+\/[^/]+/, '/shared/:owner/:id');
  return p + tail;
}

// Maintain a single <link rel="canonical"> on the document, creating it if the
// static index.html didn't include one. Called on every page view so SPA
// route changes update the canonical alongside GA4's page_view.
function setCanonicalLink(href) {
  if (typeof document === 'undefined') return;
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  if (link.getAttribute('href') !== href) {
    link.setAttribute('href', href);
  }
}

function buildPageLocation(cleanPath) {
  if (typeof window === 'undefined') return `${CANONICAL_ORIGIN}${cleanPath}`;
  // Preserve the query string from the actual URL so gclid / utm_* survive
  // into page_location — GA4 reads them from page_location to attribute the
  // session source on the first page_view.
  const query = window.location.search || '';
  return `${CANONICAL_ORIGIN}${cleanPath}${query}`;
}

const CONSENT_KEY = 'plum-cookie-consent';

let consentGiven = false;
let bootstrapped = false;

function readStoredConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
}

// Whether the user has explicitly accepted. Used only to gate sending
// personal identifiers (user_id) — page views and events flow through gtag
// regardless and rely on Consent Mode v2 to suppress cookies/identifiers
// when consent is denied.
function hasConsent() {
  if (consentGiven) return true;
  consentGiven = readStoredConsent() === 'accepted';
  return consentGiven;
}

// Boot the gtag pipeline with Consent Mode v2 defaults. Runs once at app
// startup regardless of consent so the conversion linker can capture `gclid`
// query params into a first-party cookie or URL passthrough before the user
// navigates away. Without this, ad clicks are unattributable in GA / Google
// Ads — gtag.js needs `consent` defaults queued *before* its first hit.
export function initAnalytics() {
  if (bootstrapped) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!GA_MEASUREMENT_ID && !GOOGLE_ADS_ID) return;
  bootstrapped = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };

  const stored = readStoredConsent();
  const granted = stored === 'accepted' ? 'granted' : 'denied';
  const consentDefault = {
    ad_storage: granted,
    analytics_storage: granted,
    ad_user_data: granted,
    ad_personalization: granted,
  };
  // Only delay the first hit when the user hasn't decided yet, so the cookie
  // banner has a window to upgrade consent before gtag flushes. Once they've
  // decided either way, we already know the answer.
  if (stored !== 'accepted' && stored !== 'declined') {
    consentDefault.wait_for_update = 500;
  }
  window.gtag('consent', 'default', consentDefault);
  // Pass the URL through cross-page navigations so the gclid survives without
  // cookies when ad_storage is denied.
  window.gtag('set', 'url_passthrough', true);
  window.gtag('js', new Date());

  // Seed page_location/page_path with the normalized URL so the automatic
  // page_view fired by the Ads config below (and any future config calls)
  // reports the route template rather than the raw URL containing session
  // ids. GA4 config has send_page_view: false so the auto-fired view is
  // suppressed there; we re-fire from trackPageView once the React route
  // resolves.
  const initialPath = normalizePath(
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );
  const initialLocation = buildPageLocation(initialPath);
  window.gtag('set', {
    page_path: initialPath,
    page_location: initialLocation,
  });

  if (GA_MEASUREMENT_ID) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false,
      page_path: initialPath,
      page_location: initialLocation,
    });
  }
  if (GOOGLE_ADS_ID) {
    window.gtag('config', GOOGLE_ADS_ID, {
      page_path: initialPath,
      page_location: initialLocation,
    });
  }

  if (!document.getElementById('ga-script')) {
    const script = document.createElement('script');
    script.id = 'ga-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID || GOOGLE_ADS_ID}`;
    document.head.appendChild(script);
  }
}

// Deprecated alias: existing call sites import `initGA` and we keep it
// working for backwards compatibility.
export const initGA = initAnalytics;

export function setAnalyticsConsent(accepted) {
  consentGiven = !!accepted;
  if (typeof window === 'undefined' || !window.gtag) return;
  const value = accepted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    ad_storage: value,
    analytics_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });
}

// Fire events through gtag regardless of consent state. Consent Mode v2
// (set up in initAnalytics) ensures denied consent translates to cookieless
// pings — no client_id, no cookies, no cross-site ID — which GA4 uses for
// modeled traffic and Google Ads uses for conversion modeling. The result is
// GDPR-compliant pre-consent measurement that still shows up in standard
// reports (Acquisition, Engagement, Conversions) once GA's modeling has
// enough data; it does not show up in the Realtime dashboard the same way
// consented hits do.
export function trackEvent(eventName, params = {}) {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', eventName, params);
}

export function trackPageView(path) {
  if (typeof window === 'undefined' || !window.gtag) return;
  const cleanPath = normalizePath(path);
  const cleanLocation = buildPageLocation(cleanPath);
  // Canonical drops the query string — tracking params should never be part
  // of the SEO-canonical URL.
  setCanonicalLink(`${CANONICAL_ORIGIN}${cleanPath}`);
  // Update gtag defaults so any non-page_view events that follow (custom
  // events, conversions) also report the normalized location instead of the
  // raw URL with a session id.
  window.gtag('set', {
    page_path: cleanPath,
    page_location: cleanLocation,
    page_title: document.title,
  });
  window.gtag('event', 'page_view', {
    page_path: cleanPath,
    page_location: cleanLocation,
    page_title: document.title,
  });
}

// Associate subsequent events with a stable user id in GA4 so cohorts and
// per-user conversion counts are consistent across devices and sessions.
// Gated on explicit consent because user_id is a personal identifier under
// GDPR and must not be sent under denied consent even via cookieless pings.
export function setUserId(uid) {
  if (!GA_MEASUREMENT_ID || !hasConsent() || typeof window === 'undefined' || !window.gtag) return;
  window.gtag('config', GA_MEASUREMENT_ID, { user_id: uid || null, send_page_view: false });
}

// Fire a Google Ads conversion event. `key` must be a key of
// `GOOGLE_ADS_CONVERSIONS`. No-op without gtag or a configured ads id.
// Pre-consent conversions are sent as Consent Mode v2 modeled conversions
// (no ad cookies) — Google Ads uses them for attribution modeling.
export function trackAdsConversion(key, params = {}) {
  if (typeof window === 'undefined' || !window.gtag || !GOOGLE_ADS_ID) return;
  const label = GOOGLE_ADS_CONVERSIONS[key];
  if (!label) return;
  window.gtag('event', 'conversion', {
    send_to: `${GOOGLE_ADS_ID}/${label}`,
    ...params,
  });
}

// Ordered map of the core conversion funnel. The value is the step index
// (1-based) — sent as a custom dimension on every funnel event so GA4 Funnel
// Exploration can order steps without relying on event-name lookups. New
// funnel events must be appended in business order; renumbering existing
// steps invalidates historical data.
export const FUNNEL_STEPS = {
  landing_view: 1,
  logged_in: 2,
  message_sent: 3,
  file_uploaded: 4,
  design_generated: 5,
  form_generated: 6,
  lead_form_started: 7,
  lead_form_submitted: 8,
  subscription_form_started: 9,
  subscription_form_submitted: 10,
};

// Fire a core-funnel event. Stamps `funnel_step` (the step index) and
// `is_funnel: true` so reports can filter on funnel-only traffic and order
// steps regardless of event name. Non-funnel diagnostic events (auth_start,
// sign_in/sign_up, subscription_changed, etc.) keep using trackEvent so they
// stay out of the funnel report but remain available for slicing.
export function trackFunnelEvent(name, params = {}) {
  const step = FUNNEL_STEPS[name];
  if (!step) return;
  trackEvent(name, { ...params, funnel_step: step, is_funnel: true });
}

// Fire an event at most once per (browser, storageKey) combination. Used for
// "first X" conversions (first message, first image upload, first download).
// Returns true when the event was actually fired. The localStorage dedup
// flag is functional (prevents duplicate analytics events) and is treated
// as strictly-necessary storage rather than tracking storage.
export function trackFirstTime(eventName, storageKey, params = {}) {
  const key = `plum-first-event:${storageKey}`;
  try {
    if (localStorage.getItem(key)) return false;
  } catch {
    // localStorage unavailable — fire the event but skip persistence so it
    // still shows up rather than being silently dropped.
    trackEvent(eventName, params);
    return true;
  }
  trackEvent(eventName, params);
  try { localStorage.setItem(key, String(Date.now())); } catch { /* ignore */ }
  return true;
}
